import { PosModel } from '@/types'

const round = (n: number) => Math.round(n * 100) / 100

/**
 * The unit price for this customer (§7.1, §15.2): their price book's price for this branch, else the price the
 * catalog already resolved (branch price, then the variation's). A price the cashier typed in has no book prices.
 */
export const unitPriceFor = (product: PosModel.Product, customer?: Pick<PosModel.Customer, 'price_book_id'>) => {
  const book = customer?.price_book_id ? product.book_prices?.[customer.price_book_id] : undefined
  return book && book > 0 ? book : product.price
}

/** Cart lines re-priced for the customer, before offers and totals, so the cart and the sale agree. */
export const priceLines = (lines: PosModel.CartLine[], customer?: Pick<PosModel.Customer, 'price_book_id'>) =>
  customer?.price_book_id
    ? lines.map((l) => {
        const price = unitPriceFor(l.product, customer)
        return price === l.product.price ? l : { ...l, product: { ...l.product, price } }
      })
    : lines

/** The member discount as an order discount, used when the cashier set none (discounts never stack, §13.1). */
export const memberDiscount = (customer?: Pick<PosModel.Customer, 'tier'>): PosModel.Discount | undefined =>
  customer?.tier ? { type: 'percent', value: customer.tier.discount_percentage, reason: `Member · ${customer.tier.name}` } : undefined

export interface DiscountCap {
  type: 'cash' | 'percentage'
  value: number
}

/**
 * Whether a manual discount stays within the cashier's `max_discount` (§7.2). No cap is set (0) on most staff and no
 * screen sets it yet, so 0 means no cap here.
 */
export const withinCap = (base: number, discount: PosModel.Discount | undefined, cap: DiscountCap | null) => {
  if (!cap || cap.value <= 0 || !discount) return true
  const off = discountAmount(base, discount)
  return cap.type === 'percentage' ? (base > 0 ? (off / base) * 100 : 0) <= cap.value + 0.001 : off <= cap.value + 0.001
}

export const discountAmount = (base: number, discount?: PosModel.Discount) => {
  if (!discount || discount.value <= 0) return 0
  const raw = discount.type === 'percent' ? (base * Math.min(discount.value, 100)) / 100 : discount.value
  return round(Math.min(raw, base))
}

export const lineGross = (line: PosModel.CartLine) => round(line.product.price * line.quantity)

export const lineNet = (line: PosModel.CartLine) => round(lineGross(line) - discountAmount(lineGross(line), line.discount))

/**
 * One place for the money maths, shared by the cart, the payment dialog and the receipt. Line discounts come off
 * first, the order discount is spread over lines by value, and tax applies to what is left on taxable lines.
 */
export const MAX_ADJUSTMENT = 0.99

/** The round-off kept within 0–0.99 and 2 decimals, never more than the total. */
export const clampAdjustment = (value: number, total: number) =>
  round(Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), MAX_ADJUSTMENT, Math.max(total, 0)))

export const calculateTotals = (
  lines: PosModel.CartLine[],
  orderDiscount: PosModel.Discount | undefined,
  taxRate: number,
  adjustment = 0
): PosModel.Totals => {
  const subtotal = round(lines.reduce((sum, l) => sum + lineGross(l), 0))
  const afterLines = round(lines.reduce((sum, l) => sum + lineNet(l), 0))
  const orderOff = discountAmount(afterLines, orderDiscount)
  const share = afterLines > 0 ? (afterLines - orderOff) / afterLines : 0
  // A product's own VAT (`sale_vat`, percent) wins; the demo's products have none and use the register's rate.
  const rateOf = (l: PosModel.CartLine) => (l.product.sale_vat !== undefined ? l.product.sale_vat / 100 : taxRate)
  const tax = round(lines.filter((l) => l.product.taxable).reduce((sum, l) => sum + lineNet(l) * share * rateOf(l), 0))

  const beforeAdjustment = round(afterLines - orderOff + tax)
  const adjust = clampAdjustment(adjustment, beforeAdjustment)

  return {
    subtotal,
    lineDiscounts: round(subtotal - afterLines),
    orderDiscount: orderOff,
    tax,
    adjustment: adjust,
    total: round(beforeAdjustment - adjust),
    itemCount: lines.reduce((sum, l) => sum + l.quantity, 0),
  }
}

/** Notes and coins a cashier is likely to be handed for this total, smallest first. */
export const quickTenders = (total: number) => {
  const notes = [10, 20, 50, 100, 200, 500, 1000]
  const options = new Set<number>([Math.ceil(total)])
  for (const note of notes) {
    if (note >= total) options.add(note)
    options.add(Math.ceil(total / note) * note)
  }
  return [...options].filter((n) => n >= total).sort((a, b) => a - b).slice(0, 4)
}
